import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { fmtDate, StatusBadge, PriorityBadge } from "@/components/common";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, RefreshCw, AlertCircle, GitBranch } from "lucide-react";
import { promptDialog } from "@/components/ui/confirm";

export default function MobileApprovals() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null); // taskId being acted on

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/approvals/pending");
      setRows(r.data?.data || []);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const action = async (taskId, act, label) => {
    const remarks = act !== "approve" ? (await promptDialog({ title: label, message: `Reason for ${label}?`, placeholder: "Enter a reason...", confirmText: label })) || "" : "";
    if (act !== "approve" && !remarks) return;
    setActing(taskId);
    try {
      await api.post(`/tasks/${taskId}/${act}`, { remarks });
      toast.success(`${label}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.message || "Failed"); }
    finally { setActing(null); }
  };

  return (
    <div className="px-4 pt-4 pb-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-lg font-heading font-bold text-slate-900">Approval Inbox</div>
          <div className="text-xs text-slate-400">{rows.length} task{rows.length !== 1 ? "s" : ""} pending</div>
        </div>
        <button onClick={load} className="p-2 bg-slate-100 rounded-full active:scale-95">
          <RefreshCw size={16} className="text-slate-500" />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center pt-10"><Loader2 className="animate-spin text-slate-400" /></div>
      ) : rows.length === 0 ? (
        <div className="text-center pt-16">
          <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
            <CheckCircle2 size={28} className="text-emerald-500" />
          </div>
          <div className="text-sm font-semibold text-slate-700">Inbox Clear!</div>
          <div className="text-xs text-slate-400 mt-1">No tasks awaiting your approval.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map(t => (
            <div key={t.id} className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
              <div className="p-4">
                <div className="flex items-start gap-2 mb-2 flex-wrap">
                  <span className="font-mono text-[10px] text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded">{t.task_code}</span>
                  <span className="badge-base bg-violet-50 text-violet-700 border-violet-200">
                    L{t.current_approval_level || 1}/{t.approval_total_levels || 1}
                  </span>
                  <StatusBadge status={t.status} />
                </div>
                <div className="text-sm font-semibold text-slate-900 leading-snug mb-1">{t.title}</div>
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span>By {t.assigned_to_name || "—"}</span>
                  <span>· {fmtDate(t.updated_at)}</span>
                </div>
                {t.priority_name && (
                  <div className="mt-2"><PriorityBadge priority={t.priority_name} /></div>
                )}
              </div>
              {/* Action strip */}
              <div className="border-t border-slate-100 grid grid-cols-3 divide-x divide-slate-100">
                <button
                  onClick={() => action(t.id, "approve", "Approved")}
                  disabled={acting === t.id}
                  className="py-3 text-xs font-semibold text-emerald-700 flex items-center justify-center gap-1.5 active:bg-emerald-50 transition-colors">
                  {acting === t.id ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={14} />}
                  Approve
                </button>
                <button
                  onClick={() => action(t.id, "rework", "Rework")}
                  disabled={acting === t.id}
                  className="py-3 text-xs font-semibold text-orange-600 flex items-center justify-center gap-1.5 active:bg-orange-50 transition-colors">
                  <RefreshCw size={14} /> Rework
                </button>
                <button
                  onClick={() => action(t.id, "reject", "Rejected")}
                  disabled={acting === t.id}
                  className="py-3 text-xs font-semibold text-red-600 flex items-center justify-center gap-1.5 active:bg-red-50 transition-colors">
                  <XCircle size={14} /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
